import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { Calendar as CalendarIcon, Download, RefreshCw, TrendingUp, Wallet, Clock, CreditCard, Ban } from "lucide-react";
import { format, subDays, startOfDay, endOfDay, startOfMonth } from "date-fns";

const inr = (n) => `INR ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const presets = [
  { key: "today", label: "Today", range: () => ({ from: new Date(), to: new Date() }) },
  { key: "yesterday", label: "Yesterday", range: () => ({ from: subDays(new Date(), 1), to: subDays(new Date(), 1) }) },
  { key: "last7", label: "Last 7 days", range: () => ({ from: subDays(new Date(), 6), to: new Date() }) },
  { key: "last30", label: "Last 30 days", range: () => ({ from: subDays(new Date(), 29), to: new Date() }) },
  { key: "thismonth", label: "This month", range: () => ({ from: startOfMonth(new Date()), to: new Date() }) },
];

const CashReport = () => {
  const { toast } = useToast();
  const [from, setFrom] = useState(new Date());
  const [to, setTo] = useState(new Date());
  const [dateField, setDateField] = useState("check_in");
  const [hotels, setHotels] = useState([]);
  const [hotelId, setHotelId] = useState("all");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const hs = await api.listHotels();
        setHotels(hs);
      } catch (_) {}
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        date_from: format(from, "yyyy-MM-dd"),
        date_to: format(to, "yyyy-MM-dd"),
        date_field: dateField,
      };
      if (hotelId !== "all") params.hotel_id = hotelId;
      const data = await api.cashReport(params);
      setReport(data);
    } catch (e) {
      toast({ title: "Failed to load report", description: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, dateField, hotelId]);

  const totals = report?.totals || { bookings: 0, cash_collected: 0, cash_pending: 0, other_collected: 0, cancelled: 0 };
  const rows = report?.hotels || [];

  const exportCsv = () => {
    if (!rows.length) return;
    const header = ["Hotel", "Bookings", "Cash Collected (INR)", "Cash Pending (INR)", "Other Collected (INR)", "Cancelled"];
    const lines = [header.join(",")];
    for (const r of rows) {
      lines.push([
        JSON.stringify(r.hotel_name),
        r.bookings,
        r.cash_collected,
        r.cash_pending,
        r.other_collected,
        r.cancelled,
      ].join(","));
    }
    lines.push(["TOTAL", totals.bookings, totals.cash_collected, totals.cash_pending, totals.other_collected, totals.cancelled].join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `aip-cash-report-${format(from, "yyyyMMdd")}-${format(to, "yyyyMMdd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const activePreset = useMemo(() => {
    for (const p of presets) {
      const r = p.range();
      if (format(r.from, "yyyy-MM-dd") === format(from, "yyyy-MM-dd") && format(r.to, "yyyy-MM-dd") === format(to, "yyyy-MM-dd")) {
        return p.key;
      }
    }
    return "custom";
  }, [from, to]);

  return (
    <div>
      {/* Controls */}
      <div className="bg-white border border-neutral-200 p-4 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          <div className="flex flex-wrap gap-2">
            {presets.map((p) => (
              <button
                key={p.key}
                onClick={() => { const r = p.range(); setFrom(r.from); setTo(r.to); }}
                className={`text-xs uppercase tracking-widest px-3 py-2 border transition-colors ${activePreset === p.key ? "border-[#c8102e] bg-[#c8102e] text-white" : "border-neutral-200 text-neutral-700 hover:border-neutral-400"}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex flex-1 flex-wrap gap-3 items-end">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">From</div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="rounded-none font-normal">
                    <CalendarIcon size={14} className="mr-2" /> {format(from, "d MMM yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={from} onSelect={(d) => d && setFrom(d)} initialFocus /></PopoverContent>
              </Popover>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">To</div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="rounded-none font-normal">
                    <CalendarIcon size={14} className="mr-2" /> {format(to, "d MMM yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={to} onSelect={(d) => d && setTo(d)} initialFocus /></PopoverContent>
              </Popover>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Group by date</div>
              <Select value={dateField} onValueChange={setDateField}>
                <SelectTrigger className="rounded-none w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="check_in">Check-in date</SelectItem>
                  <SelectItem value="check_out">Check-out date</SelectItem>
                  <SelectItem value="created_at">Booking date</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Hotel</div>
              <Select value={hotelId} onValueChange={setHotelId}>
                <SelectTrigger className="rounded-none w-64"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All hotels</SelectItem>
                  {hotels.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} className="rounded-none">
              <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button onClick={exportCsv} disabled={!rows.length} className="rounded-none bg-neutral-900 hover:bg-black text-white">
              <Download size={14} className="mr-2" /> Export CSV
            </Button>
          </div>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatCard icon={Wallet} label="Cash collected" value={inr(totals.cash_collected)} accent />
        <StatCard icon={Clock} label="Cash pending" value={inr(totals.cash_pending)} />
        <StatCard icon={CreditCard} label="Other (card/UPI)" value={inr(totals.other_collected)} />
        <StatCard icon={TrendingUp} label="Total bookings" value={totals.bookings} />
        <StatCard icon={Ban} label="Cancelled" value={totals.cancelled} />
      </div>

      {/* Table */}
      <div className="bg-white border border-neutral-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-100 text-neutral-700">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium">Hotel</th>
              <th className="px-4 py-3 font-medium text-right">Bookings</th>
              <th className="px-4 py-3 font-medium text-right">Cash Collected</th>
              <th className="px-4 py-3 font-medium text-right">Cash Pending</th>
              <th className="px-4 py-3 font-medium text-right">Other Collected</th>
              <th className="px-4 py-3 font-medium text-right">Cancelled</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center py-12 text-neutral-500">No bookings for the selected range.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.hotel_id} className="border-t border-neutral-100 hover:bg-neutral-50">
                <td className="px-4 py-3 font-medium">{r.hotel_name}</td>
                <td className="px-4 py-3 text-right">{r.bookings}</td>
                <td className="px-4 py-3 text-right font-medium text-emerald-700">{inr(r.cash_collected)}</td>
                <td className="px-4 py-3 text-right text-amber-700">{inr(r.cash_pending)}</td>
                <td className="px-4 py-3 text-right text-neutral-700">{inr(r.other_collected)}</td>
                <td className="px-4 py-3 text-right text-rose-700">{r.cancelled}</td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-neutral-50 font-medium">
              <tr>
                <td className="px-4 py-3">Grand Total</td>
                <td className="px-4 py-3 text-right">{totals.bookings}</td>
                <td className="px-4 py-3 text-right text-emerald-700">{inr(totals.cash_collected)}</td>
                <td className="px-4 py-3 text-right text-amber-700">{inr(totals.cash_pending)}</td>
                <td className="px-4 py-3 text-right">{inr(totals.other_collected)}</td>
                <td className="px-4 py-3 text-right text-rose-700">{totals.cancelled}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, accent = false }) => (
  <div className={`bg-white border ${accent ? "border-[#c8102e]" : "border-neutral-200"} p-5`}>
    <div className="flex items-center gap-2">
      <Icon size={14} className={accent ? "text-[#c8102e]" : "text-neutral-500"} />
      <span className="text-xs uppercase tracking-widest text-neutral-500">{label}</span>
    </div>
    <div className={`text-2xl font-serif font-medium mt-2 ${accent ? "text-[#c8102e]" : "text-neutral-900"}`}>{value}</div>
  </div>
);

export default CashReport;
